import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { AnimatePresence, motion } from 'motion/react'
import { useState } from 'react'
import { useDocumentTitle } from 'usehooks-ts'
import { z } from 'zod'
import { AppLoader } from '~/components/loader'
import { OrganizationAvatar } from '~/features/avatar'
import { LinkButtonHref } from '~/features/navigation'
import { getConfigValue } from '~/helpers/envHelpers'
import { useTRPC } from '~/services/trpc'
import { PublicTemplate } from '~/templates/public'

import { Button, Heading1, Icon } from '@hap/components'

import { DISPLAY, ICON, providers } from '../-constants'
import { ProfileForm } from './-components/profile-form'

export const Route = createFileRoute('/_public/register/')({
  component: RouteComponent,
  validateSearch: z.object({
    token: z.string(),
  }),
})

function RouteComponent() {
  const params = Route.useSearch()
  const trpc = useTRPC()
  const [view, setView] = useState<'welcome' | 'profile'>('welcome')

  useDocumentTitle('Welcome to Happitu!')

  const { data, isLoading } = useQuery(
    trpc.invite.verify.queryOptions({
      token: params.token,
    }),
  )

  if (isLoading) return <AppLoader />
  if (!data)
    return (
      <PublicTemplate width={400}>
        <div className="bg-accent text-center">
          <div className="flex flex-col content-center justify-center space-y-6 p-8 pb-16">
            <Icon
              name="warning-diamond"
              size="60"
              className="fill-earth-yellow mx-auto h-[100px]"
              weight="fill"
            />
            <p>
              Your invitation link is invalid or has expired. Please ask your
              administrator to resend the invite.
            </p>
          </div>
        </div>
      </PublicTemplate>
    )

  return (
    <PublicTemplate width={400}>
      <AnimatePresence>
        {view === 'profile' ? (
          <motion.div
            key="profile"
            className="bg-accent absolute top-1/2 w-[400px] -translate-y-1/2"
            animate={{ y: 0, opacity: 1 }}
            initial={{ y: -50, opacity: 0 }}
            exit={{ y: 50, opacity: 0 }}
          >
            <ProfileForm
              defaultEmail={data.invite.email}
              token={params.token}
              onBack={() => setView('welcome')}
            />
          </motion.div>
        ) : (
          <motion.div
            key="invite"
            className="bg-accent absolute top-1/2 w-[400px] -translate-y-1/2 text-center"
            animate={{ y: 0, opacity: 1 }}
            initial={{ y: -50, opacity: 0 }}
            exit={{ y: 50, opacity: 0 }}
          >
            <div className="flex flex-col content-center space-y-6 p-8">
              <div>
                <OrganizationAvatar {...data.organization} />
              </div>

              <Heading1>
                {data.invitedBy.firstName} has invited you to {data.organization.name}
              </Heading1>
              <p className="text-accent-foreground">
                Happitu turns every customer conversation into actionable insights because
                life's too short for mystery metrics and crossed fingers.
              </p>
            </div>

            <div className="grid space-y-3 border-t p-8">
              <p className="text-accent-foreground">
                To accept the invite please join with one of the following methods.
              </p>
              <Button size="lg" onClick={() => setView('profile')}>
                With Email
              </Button>

              {Object.entries(providers).map(([key]) => {
                const match = providers[key]
                if (match?.hide) return
                const icon = ICON[key]
                return (
                  <LinkButtonHref
                    href={`${getConfigValue('VITE_AUTH_URL')}/auth/${key}/authorize?token=${params.token}`}
                    className="flex items-center gap-2"
                    variant="secondary"
                    size="lg"
                  >
                    {icon && <i className="h-[20px] w-[20px]">{icon}</i>}
                    {DISPLAY[key] || key}
                  </LinkButtonHref>
                )
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </PublicTemplate>
  )
}
